// src/components/CustomAppControlComponent.js
import React from 'react';
import {
    Button, Card, CardContent, Typography, CircularProgress,
    FormControlLabel, Checkbox, Box, Divider
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { ACCEPTED_IMAGE_TYPES_STRING } from '../constants';

const CustomAppControlComponent = ({
    onFilesAdded,
    handleProcess,
    handleReset,
    isProcessing,
    hasFiles,
    usePublicIdentities,
    setUsePublicIdentities,
    useSavedIdentities,
    setUseSavedIdentities,
    handleCreateNewCluster,
    handleSaveIdentities,
    isLoggedIn,
    hasClusters,
    handleRunDating,
    handleRunDatingExperimental
}) => (
    <Card>
        <CardContent>
            <Typography variant="h6" component="h3" gutterBottom>
                Controls
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Step 1:</strong> Upload images<br />
                <strong>Step 2:</strong> Click Detect & Cluster Faces<br />
                <strong>Step 3:</strong> Define identities with birth years<br />
                <strong>Step 4:</strong> Click Run Dating
            </Typography>

            {/* --- Section 1: Identity Sources --- */}
            <Box sx={{ my: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Identity Sources</Typography>
                <Divider />
                <Box sx={{ mt: 1 }}>
                    {/* "My Saved Identities" is only shown when logged in */}
                    {isLoggedIn && (
                        <FormControlLabel
                            control={<Checkbox
                                checked={useSavedIdentities}
                                onChange={(e) => setUseSavedIdentities(e.target.checked)}
                                // It remains disabled after processing starts
                                disabled={isProcessing || hasClusters}
                            />}
                            label="Use My Saved Identities"
                        />
                    )}

                    {/* "CSFD Identities" is ALWAYS visible */}
                    <FormControlLabel
                        control={<Checkbox
                            checked={usePublicIdentities}
                            onChange={(e) => setUsePublicIdentities(e.target.checked)}
                            disabled={isProcessing}
                        />}
                        label="Use CSFD-1.6M Identities"
                    />
                </Box>
                <Divider />
            </Box>

            {/* --- Section 2: Main Actions --- */}
            {!hasClusters ? (
                // --- Phase 1 View: Before Clustering ---
                <>
                    <Button
                        component="label"
                        variant="contained"
                        startIcon={<UploadFileIcon />}
                        fullWidth
                    >
                        {hasFiles ? 'Add More Files' : 'Select Images'}
                        <input
                            type="file"
                            multiple
                            onChange={(e) => onFilesAdded(Array.from(e.target.files))}
                            hidden
                            accept={ACCEPTED_IMAGE_TYPES_STRING}
                        />
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleProcess}
                        disabled={isProcessing || !hasFiles}
                        sx={{ mt: 2 }}
                    >
                        {isProcessing ? <CircularProgress size={24} /> : 'Detect & Cluster Faces'}
                    </Button>
                </>
            ) : (
                // --- Phase 2 View: After Clustering ---
                <>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PlayCircleOutlineIcon />}
                        fullWidth
                        onClick={handleRunDating}
                        disabled={isProcessing}
                        sx={{ mb: 1 }}
                    >
                        {isProcessing ? <CircularProgress size={24} /> : 'Run Dating'}
                    </Button>

                    {isLoggedIn && (
                        <Button
                            variant="contained"
                            color="warning"
                            startIcon={<PlayCircleOutlineIcon />}
                            fullWidth
                            onClick={handleRunDatingExperimental}
                            disabled={isProcessing}
                            sx={{ mb: 1 }}
                        >
                            {isProcessing ? <CircularProgress size={24} /> : 'Run Dating (Experimental)'}
                        </Button>
                    )}

                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<AddIcon />}
                        fullWidth
                        onClick={handleCreateNewCluster}
                        disabled={isProcessing}
                        sx={{ mb: 1 }}
                    >
                        Create New Identity
                    </Button>
                    {isLoggedIn && (
                        <Button
                            variant="contained"
                            color="info"
                            startIcon={<SaveIcon />}
                            fullWidth
                            onClick={handleSaveIdentities}
                            disabled={isProcessing}
                        >
                            Save Identities
                        </Button>
                    )}
                </>
            )}

            {/* --- Section 3: Reset Button (Always visible if files are present) --- */}
            <Button
                variant="contained"
                color="secondary"
                fullWidth
                onClick={handleReset}
                disabled={isProcessing || !hasFiles}
                sx={{ mt: 2 }}
            >
                Start Over
            </Button>
        </CardContent>
    </Card>
);

export default CustomAppControlComponent;